// Presentational building blocks for the Messaging Reports page. They mirror
// the chart cards on the Calls page (RhinoVOIPReports) so both reports read
// the same, but are small reusable pieces instead of inline JSX per chart.
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { downloadCsv, categoryColor } from '../helpers/ReportsHelpers';
import { sortRows } from '../helpers/MessagingReportsHelpers';

export const GROUPING_OPTIONS = [
  { id: 'total', label: 'Total' },
  { id: 'day', label: 'By Day' },
  { id: 'week', label: 'By Week' },
  { id: 'month', label: 'By Month' },
];

export const ChartCard = ({ title, half, children, csv, actions }) => (
  <div className={`voip-reports__chart-card${half ? ' voip-reports__chart-card--half' : ''}`}>
    <div className="voip-reports__chart-header">
      <h3 className="voip-reports__chart-title">{title}</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {actions}
        {csv && (
          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv(csv.columns, csv.rows, csv.filename)}>
            Export CSV
          </button>
        )}
      </div>
    </div>
    {children}
  </div>
);

ChartCard.propTypes = {
  title: PropTypes.node.isRequired,
  half: PropTypes.bool,
  children: PropTypes.node,
  csv: PropTypes.shape({ columns: PropTypes.array, rows: PropTypes.array, filename: PropTypes.string }),
  actions: PropTypes.node,
};

export const EmptyChart = ({ height }) => (
  <div
    className="voip-reports__chart-empty"
    style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}
  >
    No data for this selection
  </div>
);

EmptyChart.propTypes = { height: PropTypes.number.isRequired };

export const GroupingSelector = ({ value, onChange }) => (
  <div className="voip-reports__grouping-selector">
    {GROUPING_OPTIONS.map((opt) => (
      <button
        key={opt.id}
        type="button"
        className={`voip-reports__grouping-btn${value === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
        onClick={() => onChange(opt.id)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

GroupingSelector.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

// Donut with a click-to-hide legend. `colorFor(name, originalIndex)` keeps a
// slice's color stable when other slices are hidden.
export const DonutChart = ({ data, colorFor, height, valueLabel }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const visible = useMemo(() => data.filter((d) => !hidden.has(d.name)), [data, hidden]);
  const legendPayload = useMemo(() => data.map((d, i) => ({
    value: d.name, color: colorFor(d.name, i), type: 'circle', inactive: hidden.has(d.name), id: d.name,
  })), [data, hidden, colorFor]);
  const toggle = (entry) => {
    const name = entry?.value;
    if (!name) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  if (!data.length) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={visible}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={100}
          innerRadius={50}
          isAnimationActive={false}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        >
          {visible.map((entry) => (
            <Cell key={entry.name} fill={colorFor(entry.name, data.findIndex((d) => d.name === entry.name))} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => [value, valueLabel]} />
        <Legend payload={legendPayload} onClick={toggle} />
      </PieChart>
    </ResponsiveContainer>
  );
};

DonutChart.propTypes = {
  data: PropTypes.array.isRequired,
  colorFor: PropTypes.func.isRequired,
  height: PropTypes.number,
  valueLabel: PropTypes.string,
};

DonutChart.defaultProps = { height: 300, valueLabel: 'Messages' };

// Horizontal bars, one per category row. `bars` = [{ key, name, color }];
// with a single bar and no color each row gets its own category color.
export const HorizontalBars = ({ data, bars, formatter, domain }) => {
  const height = Math.max(300, data.length * 40);
  if (!data.length) return <EmptyChart height={height} />;
  const single = bars.length === 1 && !bars[0].color;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" allowDecimals={false} domain={domain} tickFormatter={formatter} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
        <Tooltip formatter={formatter} />
        {bars.length > 1 && <Legend />}
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color} radius={[0, 4, 4, 0]}>
            {single && data.map((entry, i) => <Cell key={entry.name} fill={categoryColor(i)} />)}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

HorizontalBars.propTypes = {
  data: PropTypes.array.isRequired,
  bars: PropTypes.array.isRequired,
  formatter: PropTypes.func,
  domain: PropTypes.array,
};

// Vertical stacked bars by a label key (hour, weekday, date).
export const StackedBars = ({ data, xKey, bars, stackId, height, interval }) => {
  if (!data.some((d) => bars.some((b) => d[b.key] > 0))) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} interval={interval} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend />
        {bars.map((bar, i) => (
          <Bar
            key={bar.key}
            dataKey={bar.key}
            name={bar.name}
            fill={bar.color}
            stackId={stackId}
            radius={i === bars.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

StackedBars.propTypes = {
  data: PropTypes.array.isRequired,
  xKey: PropTypes.string.isRequired,
  bars: PropTypes.array.isRequired,
  stackId: PropTypes.string.isRequired,
  height: PropTypes.number,
  interval: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

StackedBars.defaultProps = { height: 300, interval: 'preserveEnd' };

// One line per series key with a click-to-hide legend.
export const MultiLine = ({ data, series, height, formatter }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (entry) => {
    const key = entry?.dataKey;
    if (!key) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  if (!series.length || !data.length) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
        <YAxis allowDecimals={false} />
        <Tooltip formatter={formatter} />
        <Legend onClick={toggle} />
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color || categoryColor(i)}
            strokeWidth={2}
            dot={false}
            connectNulls
            hide={hidden.has(s.key)}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

MultiLine.propTypes = {
  data: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  height: PropTypes.number,
  formatter: PropTypes.func,
};

MultiLine.defaultProps = { height: 400 };

// Sortable table used by every Table view. `columns` = [{ key, label, format? }].
export const SortableTable = ({ columns, rows, defaultSort, defaultAsc, rowKey }) => {
  const [sortColumn, setSortColumn] = useState(defaultSort);
  const [sortAsc, setSortAsc] = useState(defaultAsc);
  const sorted = useMemo(() => sortRows(rows, sortColumn, sortAsc), [rows, sortColumn, sortAsc]);
  const handleSort = (key) => {
    if (key === sortColumn) setSortAsc((prev) => !prev);
    else { setSortColumn(key); setSortAsc(true); }
  };
  return (
    <div className="voip-reports__table-container">
      <table className="voip-reports__table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="voip-reports__th--sortable" onClick={() => handleSort(col.key)}>
                {col.label}
                <span className={`voip-reports__sort-arrows${sortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                  <span className={sortColumn === col.key && sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                  <span className={sortColumn === col.key && !sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: '#888' }}>No data for this selection</td></tr>
          )}
          {sorted.map((row, idx) => (
            <tr key={rowKey ? rowKey(row, idx) : idx}>
              {columns.map((col) => <td key={col.key}>{col.format ? col.format(row) : row[col.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SortableTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  defaultSort: PropTypes.string.isRequired,
  defaultAsc: PropTypes.bool,
  rowKey: PropTypes.func,
};

SortableTable.defaultProps = { defaultAsc: false };

// CSV column list from table columns (same keys/labels/formatters).
export const csvColumns = (columns) => columns.map((c) => ({ key: c.key, header: c.label, format: c.format }));
